/**
 * Record a stream as a sequence of independently decodable audio segments.
 *
 * Why this exists: `MediaRecorder.start(timeslice)` looks like it produces chunks,
 * but it does not produce *files*. Only the first blob carries the container
 * header, so blob 7 on its own is undecodable — which is fine when the browser
 * concatenates them all at the end, and useless when the point is to transcribe
 * blob 7 the moment it exists.
 *
 * So instead of slicing one recorder's output, this rotates recorders. Each
 * recorder is started and stopped in full, so each segment is a complete file that
 * any decoder (and Gemini) can read on its own.
 *
 * Rotation overlaps on purpose. The next recorder starts a few seconds *before* the
 * current one stops, both reading the same MediaStream, so:
 *
 *   - no speech falls into the gap between "stop A" and "start B", and
 *   - the seam is transcribed twice, which is what lets the server-side stitcher
 *     carry a speaker's name across the boundary instead of restarting at
 *     "Speaker 1" every segment.
 *
 * The duplicated words are removed during stitching; the duplicated *audio* is a
 * few seconds per segment, which is the price of not losing a sentence.
 */

export interface RecordedSegment {
  blob: Blob;
  index: number;
  /** Seconds from the start of the meeting. */
  startTime: number;
  endTime: number;
  duration: number;
  mimeType: string;
  isFinal: boolean;
}

export interface SegmentedRecorderOptions {
  stream: MediaStream;
  mimeType: string;
  onSegment: (segment: RecordedSegment) => void;
  onError?: (error: unknown) => void;
  /** Nominal segment length. Shorter = fresher transcript, more requests. */
  segmentSeconds?: number;
  /** How long both recorders run at a rotation. Must be < segmentSeconds. */
  overlapSeconds?: number;
  /** Injectable for tests; defaults to the platform MediaRecorder. */
  recorderFactory?: (stream: MediaStream, options: MediaRecorderOptions) => MediaRecorder;
  now?: () => number;
}

export const DEFAULT_SEGMENT_SECONDS = 60;

/**
 * Long enough that a whole speaker turn usually sits inside the window — that is
 * what the stitcher needs to match a voice across the seam — and short enough that
 * the duplicated audio stays a rounding error on cost.
 */
export const DEFAULT_OVERLAP_SECONDS = 6;

interface ActiveRecorder {
  recorder: MediaRecorder;
  index: number;
  startedAt: number;
  blobs: Blob[];
}

export class SegmentedRecorder {
  private readonly options: Required<
    Pick<SegmentedRecorderOptions, 'segmentSeconds' | 'overlapSeconds'>
  > & SegmentedRecorderOptions;

  private active: ActiveRecorder | null = null;
  private rotationTimer: ReturnType<typeof setTimeout> | null = null;
  private nextIndex = 0;
  private startedAt = 0;
  /** Meeting-clock seconds already elapsed, excluding the current run. */
  private elapsedBeforeRun = 0;
  private running = false;
  private stopping = false;
  private stopResolvers: Array<() => void> = [];
  private pendingStops = 0;

  constructor(options: SegmentedRecorderOptions) {
    const segmentSeconds = Math.max(5, options.segmentSeconds ?? DEFAULT_SEGMENT_SECONDS);
    this.options = {
      ...options,
      segmentSeconds,
      // An overlap at or past half the segment would make segments advance slower
      // than they are cut, which never terminates.
      overlapSeconds: Math.max(
        0,
        Math.min(options.overlapSeconds ?? DEFAULT_OVERLAP_SECONDS, segmentSeconds / 2),
      ),
    };
  }

  private now() {
    return (this.options.now ?? (() => Date.now()))();
  }

  private create(stream: MediaStream, mimeType: string): MediaRecorder {
    const factory = this.options.recorderFactory
      ?? ((source: MediaStream, opts: MediaRecorderOptions) => new MediaRecorder(source, opts));
    return factory(stream, { mimeType });
  }

  /** Meeting-clock position, in seconds, right now. */
  private clock(): number {
    if (!this.running) return this.elapsedBeforeRun;
    return this.elapsedBeforeRun + (this.now() - this.startedAt) / 1000;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.stopping = false;
    this.startedAt = this.now();
    this.beginSegment();
  }

  private beginSegment() {
    if (!this.running) return;
    const index = this.nextIndex++;
    const startTime = this.clock();

    let recorder: MediaRecorder;
    try {
      recorder = this.create(this.options.stream, this.options.mimeType);
    } catch (error) {
      this.options.onError?.(error);
      return;
    }

    const entry: ActiveRecorder = { recorder, index, startedAt: startTime, blobs: [] };
    this.active = entry;
    this.pendingStops += 1;

    recorder.ondataavailable = event => {
      if (event.data && event.data.size > 0) entry.blobs.push(event.data);
    };
    recorder.onerror = event => {
      this.options.onError?.(event);
    };
    recorder.onstop = () => {
      const endTime = this.clock();
      const blob = new Blob(entry.blobs, { type: this.options.mimeType });
      entry.blobs = [];
      // A zero-byte segment is a recorder that never produced anything; emitting it
      // would make the server count a chunk that can never transcribe, and the
      // missing-chunk check would then block finalize forever.
      if (blob.size > 0) {
        this.options.onSegment({
          blob,
          index: entry.index,
          startTime: entry.startedAt,
          endTime,
          duration: Math.max(0, endTime - entry.startedAt),
          mimeType: this.options.mimeType,
          isFinal: this.stopping && this.pendingStops <= 1,
        });
      }
      this.pendingStops = Math.max(0, this.pendingStops - 1);
      if (this.stopping && this.pendingStops === 0) {
        const resolvers = this.stopResolvers;
        this.stopResolvers = [];
        resolvers.forEach(resolve => resolve());
      }
    };

    try {
      recorder.start();
    } catch (error) {
      this.pendingStops = Math.max(0, this.pendingStops - 1);
      this.options.onError?.(error);
      return;
    }

    this.scheduleRotation(entry);
  }

  private scheduleRotation(entry: ActiveRecorder) {
    this.clearRotation();
    const { segmentSeconds, overlapSeconds } = this.options;
    // Fire when the *next* segment should begin, i.e. one overlap before this one
    // is due to stop.
    const untilNextStart = (segmentSeconds - overlapSeconds) * 1000;
    this.rotationTimer = setTimeout(() => {
      if (!this.running || this.stopping || this.active !== entry) return;
      // Start the successor first, so the stream is being captured by two
      // recorders across the seam and no audio is lost between them.
      this.beginSegment();
      setTimeout(() => {
        if (entry.recorder.state !== 'inactive') {
          try {
            entry.recorder.stop();
          } catch (error) {
            this.options.onError?.(error);
          }
        }
      }, overlapSeconds * 1000);
    }, untilNextStart);
  }

  private clearRotation() {
    if (this.rotationTimer !== null) {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }
  }

  pause() {
    if (!this.running || this.stopping) return;
    this.clearRotation();
    // Freeze the meeting clock so a 10-minute pause does not appear in the
    // transcript's timestamps as ten minutes of silence.
    this.elapsedBeforeRun = this.clock();
    this.running = false;
    const entry = this.active;
    if (entry && entry.recorder.state === 'recording') {
      try {
        entry.recorder.pause();
      } catch (error) {
        this.options.onError?.(error);
      }
    }
  }

  resume() {
    if (this.running || this.stopping) return;
    this.running = true;
    this.startedAt = this.now();
    const entry = this.active;
    if (entry && entry.recorder.state === 'paused') {
      try {
        entry.recorder.resume();
      } catch (error) {
        this.options.onError?.(error);
      }
      this.scheduleRotation(entry);
      return;
    }
    this.beginSegment();
  }

  /** Stop every recorder and resolve once the last segment has been emitted. */
  stop(): Promise<void> {
    if (this.stopping) {
      return new Promise(resolve => this.stopResolvers.push(resolve));
    }
    this.stopping = true;
    this.clearRotation();
    const promise: Promise<void> = this.pendingStops === 0
      ? Promise.resolve()
      : new Promise(resolve => this.stopResolvers.push(resolve));

    const entry = this.active;
    if (entry && entry.recorder.state !== 'inactive') {
      try {
        entry.recorder.stop();
      } catch (error) {
        this.options.onError?.(error);
        this.pendingStops = 0;
      }
    }
    this.running = false;
    this.active = null;
    return promise;
  }

  /** Tear down without waiting; used when the component unmounts mid-recording. */
  dispose() {
    this.clearRotation();
    const entry = this.active;
    if (entry) {
      entry.recorder.ondataavailable = null;
      entry.recorder.onstop = null;
      entry.recorder.onerror = null;
      if (entry.recorder.state !== 'inactive') {
        try {
          entry.recorder.stop();
        } catch {
          // A recorder that refuses to stop is being discarded anyway.
        }
      }
    }
    this.active = null;
    this.running = false;
    this.stopping = false;
    this.stopResolvers = [];
    this.pendingStops = 0;
  }

  get segmentCount() {
    return this.nextIndex;
  }

  get elapsedSeconds() {
    return this.clock();
  }
}

export default SegmentedRecorder;
